import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, ChevronDown, Facebook, Instagram, Linkedin } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useScrolled } from "../../hooks/useScrolled.js";
import { usePageVisibility } from "../../hooks/usePageVisibility.js";
import { fetchServices, fetchDisplayHomes } from "../../api/public.api.js";
import { Container } from "./Container.jsx";
import { BRAND, SOCIAL } from "../../lib/brand.js";
import { cn } from "../../lib/cn.js";

// Dropdown children carry only `label` + `to` — descriptions are
// intentionally omitted so the menus stay tight (one line per entry).
const STATIC_NAV = [
  { label: "Home", to: "/" },
  { label: "Home Designs", to: "/home-designs" },
  { label: "House & Land", to: "/house-land" },
  // Display Homes slot — children populated from public list, grouped by location.
  { label: "Display Homes", to: "/display-homes", _displayHomesSlot: true },
  { label: "Facades", to: "/facades" },
  { label: "Floor Plans", to: "/floor-plans" },
  { label: "Gallery", to: "/gallery" },
  // Services slot — children populated from /public/services.
  { label: "Services", to: "/services", _servicesSlot: true },
  {
    label: "About",
    to: "/about/company-profile",
    children: [
      { label: "Company Profile", to: "/about/company-profile" },
      { label: "Why Build with Us", to: "/about/why-build-with-us" },
      { label: "Where We Build", to: "/about/where-we-build" },
    ],
  },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact-us" },
];

// Slug helper — must match the one used by DisplayHomesPage so URLs line up.
const toSlug = (s) =>
  String(s ?? "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

function useServicesNavChildren() {
  const { data } = useQuery({
    queryKey: ["public", "services", "nav"],
    queryFn: fetchServices,
    staleTime: 10 * 60_000,
  });
  return useMemo(() => {
    const all = [{ label: "All services", to: "/services" }];
    if (Array.isArray(data)) {
      for (const s of data.slice(0, 8)) {
        all.push({ label: s.title, to: `/services/${s.slug}` });
      }
    }
    return all;
  }, [data]);
}

function useDisplayHomesNavChildren() {
  const { data } = useQuery({
    queryKey: ["public", "display-homes", "nav"],
    queryFn: () => fetchDisplayHomes({ limit: 50 }),
    staleTime: 10 * 60_000,
  });
  return useMemo(() => {
    const all = [{ label: "All display homes", to: "/display-homes" }];
    const seen = new Set();
    const homes = data?.items ?? [];
    for (const h of homes) {
      const name = h.locationGroup || "Other locations";
      const slug = toSlug(name);
      if (seen.has(slug)) continue;
      seen.add(slug);
      all.push({ label: name, to: `/display-homes/location/${slug}` });
      if (all.length >= 9) break;
    }
    return all;
  }, [data]);
}

export function Header() {
  const scrolled = useScrolled(24);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const servicesChildren = useServicesNavChildren();
  const displayHomesChildren = useDisplayHomesNavChildren();
  const visibility = usePageVisibility();
  const NAV = useMemo(
    () =>
      STATIC_NAV
        // Drop hidden top-level pages BEFORE we resolve any slots so we
        // don't waste effort building dropdowns for entries we'll never
        // render.
        .filter((item) => {
          if (item.to === "/display-homes" && !visibility.displayHomes) return false;
          if (item.to === "/house-land" && !visibility.houseLand) return false;
          return true;
        })
        .map((item) => {
          if (item._servicesSlot)
            return { label: item.label, to: item.to, children: servicesChildren };
          if (item._displayHomesSlot)
            return { label: item.label, to: item.to, children: displayHomesChildren };
          return item;
        }),
    [
      servicesChildren,
      displayHomesChildren,
      visibility.displayHomes,
      visibility.houseLand,
    ]
  );

  // Close the drawer on path change (works when nav links navigate away).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the drawer is open so the page underneath
  // doesn't scroll when the user swipes inside the drawer.
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
    return undefined;
  }, [mobileOpen]);

  // Escape closes the drawer — nice-to-have for keyboard users + tablet.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  const isHome = location.pathname === "/";
  const isHomeStacked = isHome && !scrolled;

  const brand = BRAND;
  const phone = brand.phone;
  const email = brand.email;
  const social = SOCIAL;

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-40 transition-all duration-300",
        scrolled
          ? "bg-bg-base/95 backdrop-blur-md border-b border-border-subtle shadow-[0_8px_24px_-12px_rgba(0,0,0,0.6)]"
          // Stronger gradient + subtle blur when sitting over the bright hero
          // image so the gold accent and white nav links stay legible.
          : "bg-gradient-to-b from-bg-base/85 via-bg-base/40 to-transparent backdrop-blur-[2px]"
      )}
    >
      {/* Top utility strip — phone, email, social. Hidden when scrolled. */}
      <div
        className={cn(
          "hidden border-b border-accent/20 bg-bg-base/95 backdrop-blur-md lg:block transition-all duration-300 overflow-hidden",
          scrolled ? "max-h-0 opacity-0" : "max-h-12 opacity-100"
        )}
      >
        <Container className="flex h-10 items-center justify-between text-xs">
          <div className="flex items-center gap-6 text-text-secondary">
            {phone && (
              <a
                href={`tel:${phone.replace(/\s+/g, "")}`}
                className="inline-flex items-center gap-1.5 hover:text-accent"
              >
                <Phone className="h-3 w-3" />
                {phone}
              </a>
            )}
            {email && (
              <a
                href={`mailto:${email}`}
                className="inline-flex items-center gap-1.5 hover:text-accent"
              >
                <Mail className="h-3 w-3" />
                {email}
              </a>
            )}
            <span className="hidden xl:inline">Mon–Fri 9am – 5pm · Sat by appointment</span>
          </div>
          <div className="flex items-center gap-3 text-text-secondary">
            {social.facebook && (
              <a href={social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-accent">
                <Facebook className="h-3.5 w-3.5" />
              </a>
            )}
            {social.instagram && (
              <a href={social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-accent">
                <Instagram className="h-3.5 w-3.5" />
              </a>
            )}
            {social.linkedin && (
              <a href={social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="hover:text-accent">
                <Linkedin className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </Container>
      </div>

      {/* Main bar.

          Homepage gets a unique stacked layout (logo centered above the nav)
          when not scrolled — that's the "emerging" look the design calls for.
          Once the user scrolls past the threshold we collapse back into the
          compact horizontal bar so navigation stays quick to reach. */}
      {isHomeStacked ? (
        <Container className="flex flex-col items-center gap-3 py-4 lg:gap-4 lg:py-6">
          {/* Mobile bar: logo on the left, CTA in the centre, menu on the right.
              Stays consistent across the homepage and other routes. */}
          <MobileBar
            brand={brand}
            onOpen={() => setMobileOpen(true)}
            className="xl:hidden"
          />
          <Link
            to="/"
            className="group hidden xl:inline-flex"
            aria-label={brand.name ?? "Shere-e-Punjab Homes"}
          >
            <img
              src="/logo.png"
              alt={brand.name ?? "Shere-e-Punjab Homes"}
              className="h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-105 xl:h-24"
            />
          </Link>
          <nav
            className="hidden flex-wrap items-center justify-center gap-1 xl:flex"
            aria-label="Primary"
          >
            {NAV.map((item) => (
              <NavItem key={item.to} item={item} />
            ))}
            <Link
              to="/request-an-appointment"
              className="ml-3 inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-2 text-sm font-medium text-bg-base transition-all hover:bg-accent-hover hover:shadow-[0_8px_24px_-8px_rgba(212,175,55,0.6)]"
            >
              Book a visit
            </Link>
          </nav>
        </Container>
      ) : (
        <>
          <Container className="hidden h-16 items-center justify-between gap-4 xl:flex lg:h-20">
            <Link
              to="/"
              className="group flex items-center shrink-0"
              aria-label={brand.name ?? "Shere-e-Punjab Homes"}
            >
              <img
                src="/logo.png"
                alt={brand.name ?? "Shere-e-Punjab Homes"}
                className="h-12 w-auto object-contain transition-transform group-hover:scale-105 lg:h-16"
              />
            </Link>

            <nav className="flex items-center gap-1" aria-label="Primary">
              {NAV.map((item) => (
                <NavItem key={item.to} item={item} />
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <Link
                to="/request-an-appointment"
                className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-2.5 text-sm font-medium text-bg-base transition-all hover:bg-accent-hover hover:shadow-[0_8px_24px_-8px_rgba(212,175,55,0.6)]"
              >
                Book a visit
              </Link>
            </div>
          </Container>
          <Container className="xl:hidden">
            <MobileBar brand={brand} onOpen={() => setMobileOpen(true)} className="h-16" />
          </Container>
        </>
      )}

      {/* Mobile drawer is rendered via a portal directly on document.body,
          NOT inside the <header>'s stacking context. The header is a fixed
          z-40 element which creates its own stacking context — putting the
          drawer inside it caused some browsers to clip or layer it behind
          page content. The portal sidesteps that entirely. */}
      <MobileDrawer
        open={mobileOpen}
        onClose={closeMobile}
        nav={NAV}
        brand={brand}
        phone={phone}
        email={email}
      />
    </header>
  );
}

/**
 * Mobile drawer — slide-in from the right, full-height, with backdrop.
 * Renders into document.body via createPortal so the header's z-index has
 * no influence on its layering. Every interactive child closes the drawer
 * on tap (`onClose`) so a tap on the active route (where path doesn't
 * change) still dismisses the menu.
 */
function MobileDrawer({ open, onClose, nav, brand, phone, email }) {
  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] xl:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Site navigation"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div className="absolute right-0 top-0 flex h-full w-[88%] max-w-sm flex-col overflow-y-auto border-l border-border bg-bg-elevated shadow-[-12px_0_32px_-12px_rgba(0,0,0,0.6)]">
        <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
          <Link to="/" onClick={onClose} aria-label="Home">
            <img
              src="/logo.png"
              alt={brand.name ?? "Shere-e-Punjab Homes"}
              className="h-10 w-auto object-contain"
            />
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-text-secondary hover:bg-bg-elevated-2 hover:text-accent"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto p-3" aria-label="Mobile">
          {nav.map((item) => (
            <MobileNavItem key={item.to} item={item} onNavigate={onClose} />
          ))}
        </nav>
        <div className="space-y-2 border-t border-border-subtle p-4">
          {phone && (
            <a
              href={`tel:${phone.replace(/\s+/g, "")}`}
              className="flex items-center gap-2 px-3 py-2 text-sm text-text-secondary"
              onClick={onClose}
            >
              <Phone className="h-4 w-4 text-accent" /> {phone}
            </a>
          )}
          {email && (
            <a
              href={`mailto:${email}`}
              className="flex items-center gap-2 px-3 py-2 text-sm text-text-secondary"
              onClick={onClose}
            >
              <Mail className="h-4 w-4 text-accent" /> {email}
            </a>
          )}
          <Link
            to="/request-an-appointment"
            onClick={onClose}
            className="mt-1 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-accent px-4 py-3 text-sm font-medium text-bg-base hover:bg-accent-hover"
          >
            Book a visit
          </Link>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Mobile header bar — three slots:
 *   • Left: brand logo (compact size, real size kept on tap target)
 *   • Centre: "Book a visit" CTA (so the primary action is always thumb-reach)
 *   • Right: hamburger that opens the slide-in drawer
 */
function MobileBar({ brand, onOpen, className = "" }) {
  return (
    <div
      className={cn("grid w-full grid-cols-[auto_1fr_auto] items-center gap-3", className)}
    >
      <Link
        to="/"
        className="group justify-self-start"
        aria-label={brand.name ?? "Shere-e-Punjab Homes"}
      >
        <img
          src="/logo.png"
          alt={brand.name ?? "Shere-e-Punjab Homes"}
          className="h-11 w-auto object-contain transition-transform group-hover:scale-105"
        />
      </Link>
      <div className="justify-self-center">
        <Link
          to="/request-an-appointment"
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3.5 py-2 text-xs font-medium text-bg-base transition-colors hover:bg-accent-hover sm:text-sm"
        >
          Book a visit
        </Link>
      </div>
      <button
        type="button"
        onClick={onOpen}
        className="justify-self-end inline-flex h-10 w-10 items-center justify-center rounded-md text-text-primary hover:bg-bg-elevated"
        aria-label="Open menu"
      >
        <Menu className="h-6 w-6" />
      </button>
    </div>
  );
}

function NavItem({ item }) {
  const [open, setOpen] = useState(false);
  const hasChildren = item.children?.length > 0;
  const location = useLocation();
  const isActive =
    item.to === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(item.to.split("?")[0]);

  if (!hasChildren) {
    return (
      <NavLink
        to={item.to}
        end={item.to === "/"}
        className={({ isActive: a }) =>
          cn(
            "relative px-3 py-2 text-sm font-medium transition-colors",
            a ? "text-accent" : "text-text-primary/90 hover:text-accent"
          )
        }
      >
        {({ isActive: a }) => (
          <>
            {item.label}
            <span
              className={cn(
                "pointer-events-none absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-accent transition-transform duration-300",
                a ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
              )}
            />
          </>
        )}
      </NavLink>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className={cn(
          "relative flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors",
          isActive ? "text-accent" : "text-text-primary/90 hover:text-accent"
        )}
        aria-expanded={open}
      >
        {item.label}
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 transition-transform",
            open && "rotate-180"
          )}
        />
        <span
          className={cn(
            "pointer-events-none absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-accent transition-transform duration-300",
            isActive ? "scale-x-100" : "scale-x-0"
          )}
        />
      </button>
      <div
        className={cn(
          "absolute left-1/2 top-full -translate-x-1/2 pt-2 transition-all",
          open ? "visible opacity-100 translate-y-0" : "invisible opacity-0 -translate-y-1"
        )}
      >
        <div className="min-w-[220px] overflow-hidden rounded-xl border border-border-subtle bg-bg-elevated shadow-[0_24px_48px_-12px_rgba(0,0,0,0.7)] backdrop-blur-md">
          {item.children.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              className={({ isActive: a }) =>
                cn(
                  "block border-b border-border-subtle/40 px-4 py-2.5 text-sm font-medium transition-colors last:border-b-0",
                  a
                    ? "bg-accent/10 text-accent"
                    : "text-text-secondary hover:bg-bg-elevated-2 hover:text-text-primary"
                )
              }
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileNavItem({ item, onNavigate }) {
  const [open, setOpen] = useState(false);
  const hasChildren = item.children?.length > 0;

  return (
    <div className="border-b border-border-subtle/40 last:border-b-0">
      <div className="flex items-center">
        <NavLink
          to={item.to}
          end={item.to === "/"}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex-1 px-3 py-3 text-base font-medium",
              isActive ? "text-accent" : "text-text-primary"
            )
          }
        >
          {item.label}
        </NavLink>
        {hasChildren && (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-text-secondary hover:text-accent"
            aria-label={open ? "Collapse" : "Expand"}
            aria-expanded={open}
          >
            <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
          </button>
        )}
      </div>
      {hasChildren && open && (
        <div className="mb-2 flex flex-col rounded-lg bg-bg-elevated-2/50 px-2 py-1">
          {item.children.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  "rounded-md px-3 py-2 text-sm",
                  isActive ? "text-accent" : "text-text-secondary hover:text-text-primary"
                )
              }
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}